import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  FileText,
  Sparkles,
  Loader2,
  Check,
  ArrowRight,
  FileOutput,
  Code2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GenerateDocumentDialog({ 
  open, 
  onOpenChange, 
  caseId, 
  caseData, 
  folders,
  onGenerated 
}) {
  const { token } = useAuth();

  // State
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [outputFilename, setOutputFilename] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [step, setStep] = useState(1); // 1: Select template, 2: Preview & Generate

  // Fetch templates when dialog opens
  useEffect(() => {
    if (open) {
      fetchTemplates();
      setStep(1);
      setSelectedTemplateId('');
      setSelectedTemplate(null);
      setPreviewData(null);
      setOutputFilename('');
      setSelectedFolderId('');
    }
  }, [open, token]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/case-management/document-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = async (templateId) => {
    setSelectedTemplateId(templateId);
    const template = templates.find(t => t.id === templateId);
    setSelectedTemplate(template);
    
    // Set default output filename
    if (template && caseData) {
      const today = new Date().toISOString().split('T')[0].replace(/-/g, '');
      setOutputFilename(`${template.name}_${caseData.case_number}_${today}.docx`);
    }
  };

  const goToPreview = async () => {
    if (!selectedTemplateId) {
      toast.error('Please select a template');
      return;
    }

    // Fetch preview data
    try {
      const res = await fetch(
        `${API_URL}/api/case-management/cases/${caseId}/documents/preview/preview-merge?template_id=${selectedTemplateId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setPreviewData(data);
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
    }

    setStep(2);
  };

  const generateDocument = async () => {
    setGenerating(true);
    try {
      const params = new URLSearchParams({
        template_id: selectedTemplateId
      });
      if (outputFilename) params.append('output_filename', outputFilename);
      if (selectedFolderId) params.append('folder_id', selectedFolderId);

      const res = await fetch(
        `${API_URL}/api/case-management/cases/${caseId}/documents/generate-from-template?${params}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (res.ok) {
        const doc = await res.json();
        toast.success('Document generated successfully!');
        onOpenChange(false);
        if (onGenerated) onGenerated(doc);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to generate document');
      }
    } catch (error) {
      toast.error('Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Generate Document from Template
          </DialogTitle>
          <DialogDescription>
            {step === 1 
              ? 'Select a template to generate a document with case data'
              : 'Review the merge fields and generate your document'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                <span>Loading templates...</span>
              </div>
            ) : templates.length > 0 ? (
              <div className="space-y-3">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleTemplateSelect(template.id)}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedTemplateId === template.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-slate-200 hover:border-purple-300 hover:bg-slate-50'
                    }`}
                    data-testid={`template-option-${template.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedTemplateId === template.id
                          ? 'bg-purple-200'
                          : 'bg-slate-100'
                      }`}>
                        <FileText className={`w-5 h-5 ${
                          selectedTemplateId === template.id
                            ? 'text-purple-600'
                            : 'text-slate-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium">{template.name}</p>
                        {template.description && (
                          <p className="text-sm text-muted-foreground">
                            {template.description}
                          </p>
                        )}
                        {template.merge_fields?.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {template.merge_fields.length} merge fields
                          </p>
                        )}
                      </div>
                    </div>
                    {selectedTemplateId === template.id && (
                      <Check className="w-5 h-5 text-purple-600" />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">
                  No document templates yet. Upload templates from the Case Management page.
                </p>
              </div>
            )}
          </div>
        )}

        {step === 2 && selectedTemplate && (
          <div className="space-y-4 py-4">
            {/* Template info */}
            <div className="p-3 bg-purple-50 rounded-lg flex items-center gap-3">
              <FileText className="w-5 h-5 text-purple-600" />
              <div>
                <p className="font-medium text-purple-800">{selectedTemplate.name}</p>
                <p className="text-sm text-purple-600">
                  {selectedTemplate.merge_fields?.length || 0} merge fields will be filled
                </p>
              </div>
            </div>

            {/* Output filename */}
            <div className="space-y-2">
              <Label>Output Filename</Label>
              <Input
                value={outputFilename}
                onChange={(e) => setOutputFilename(e.target.value)}
                placeholder="document.docx"
                data-testid="output-filename-input"
              />
            </div>

            {/* Folder selection */}
            <div className="space-y-2">
              <Label>Save to Folder</Label>
              <Select value={selectedFolderId} onValueChange={setSelectedFolderId}>
                <SelectTrigger data-testid="folder-select">
                  <SelectValue placeholder="Root folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Root folder</SelectItem>
                  {folders?.map((folder) => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Preview merge fields */}
            {selectedTemplate.merge_fields?.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Code2 className="w-4 h-4" />
                  Merge Fields Preview
                </Label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {selectedTemplate.merge_fields.map((field, idx) => {
                      const value = previewData?.preview_values?.[field] || 
                                   (caseData && getMergeValue(field, caseData)) || 
                                   '[Will be filled]';
                      return (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <code className="px-2 py-0.5 bg-slate-100 rounded font-mono text-xs">
                            {field}
                          </code>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground max-w-[200px] truncate">
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={goToPreview} 
                disabled={!selectedTemplateId}
                data-testid="continue-to-preview-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button 
                onClick={generateDocument} 
                disabled={generating}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="generate-document-btn"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <FileOutput className="w-4 h-4 mr-2" />
                )}
                Generate Document
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Helper function to get merge values from case data
function getMergeValue(field, caseData) {
  const today = new Date();
  const mapping = {
    '{{case_name}}': caseData.name,
    '{{case_number}}': caseData.case_number,
    '{{case_type}}': caseData.case_type?.replace('_', ' '),
    '{{current_stage}}': caseData.current_stage?.replace('_', ' '),
    '{{client_name}}': caseData.client_name,
    '{{opposing_party}}': caseData.opposing_party,
    '{{today_date}}': today.toLocaleDateString(),
    '{{today_date_long}}': today.toLocaleDateString('en-US', { 
      year: 'numeric', month: 'long', day: 'numeric' 
    }),
    '{{court_name}}': caseData.court_name,
    '{{judge_name}}': caseData.judge_name,
    '{{fee_type}}': caseData.fee_type?.replace('_', ' '),
    '{{hourly_rate}}': caseData.hourly_rate ? `$${caseData.hourly_rate}` : '',
    '{{total_billed}}': `$${caseData.total_billed || 0}`,
    '{{total_collected}}': `$${caseData.total_collected || 0}`,
  };
  return mapping[field] || '';
}
